import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = [
    "lineItemsContainer",
    "lineItemTemplate",
    "lineItem",
    "lineItemId",
    "lineItemContent",
    "destroy",
    "removalNotice",
    "removalModal",
    "removeButton",
    "productSelect",
    "quantity",
    "unitCost",
    "lineTotal",
    "discountFieldWrapper",
    "discountContainer",
    "discountPrefix",
    "discountAmount",
    "discountType",
    "syncStatus",
    "validationErrors",
    "errorList",
    "cybershipPreviewSection",
    "localPreviewResults",
    "localPreviewRefreshButton",
    "cybershipPreviewResults",
    "cybershipRecompareButton",
    "cybershipPreviewChecked",
    "saveButton",
    "saveBlockedNotice"
  ]

  static values = {
    requiresCybershipCheck: Boolean,
    localPreviewUrl: String,
    cybershipPreviewUrl: String
  }

  connect() {
    this.errors = []
    this.pendingRemovalLineItem = null
    this.cybershipCheckCompleted = false
    this.localPreviewTimer = null

    this.updateLineItemNumbers()
    this.calculateAllLineTotals()

    if (this.hasDiscountTypeTarget && this.hasDiscountAmountTarget) {
      this.originalDiscountType = this.discountTypeTarget.value
      this.originalDiscountAmount = this.discountAmountTarget.value
    }

    this.updateDiscountDisplay()
    this.updateOrderSummary()

    if (this.hasCybershipPreviewCheckedTarget) {
      this.cybershipCheckCompleted = this.cybershipPreviewCheckedTarget.value === "1"
    }
    this.updateSaveButtonState()

    this.element.addEventListener('typeahead:selected', this.handleProductSelected.bind(this))

    this.element.addEventListener('click', (e) => {
      if (e.target.closest('button[data-product-sku]')) {
        e.preventDefault()
        this.refreshProductSyncStatus(e)
      }
    })
  }

  // erik/TODO: Probably want to clean this up, but I'm really not too worried
  // about it given that it's in the admin section and not a user facing issue
  // that's likely to require a lot of maintenance or change in the future.
  // Can come back to this if we really need to later on.
  // Had a bunch of issues with this and the numbering of the elements, need to
  // mess with the DOM more than I would like to here to get this to work.
  addLineItem() {
    // Grab the hidden template row's HTML
    let content = this.lineItemTemplateTarget.innerHTML

    // Generate a unique timestamp-based index to replace NEW_RECORD
    const newId = Date.now()

    // - Replace the placeholder index
    // - Strip any disabled attributes so the new inputs are enabled & validated
    let newContent = content
      .replace(/NEW_RECORD/g, newId)
      .replace(/ disabled="disabled"/g, '')
      .replace(/ disabled=""/g, '')

    // Insert the enabled clone into the DOM
    this.lineItemsContainerTarget.insertAdjacentHTML('beforeend', newContent)

    this.invalidateCybershipCheck()
    this.scheduleLocalPreview()
    this.updateLineItemNumbers()
    this.calculateAllLineTotals()
  }

  removeLineItem(event) {
    event.preventDefault()
    event.stopPropagation()

    const lineItem = event.target.closest('[data-purchase-order-form-target="lineItem"]')
    if (!lineItem) return

    const destroyField = lineItem.querySelector('[data-purchase-order-form-target="destroy"]')
    if (!destroyField || this.isMarkedForDestroy(destroyField)) return

    if (this.isPersistedLineItem(lineItem)) {
      if (!this.hasRemovalModalTarget) {
        this.markLineItemForRemoval(lineItem)
        this.invalidateCybershipCheck()
        this.scheduleLocalPreview()
        this.updateLineItemNumbers()
        this.calculateAllLineTotals()
        return
      }

      this.pendingRemovalLineItem = lineItem
      this.removalModalTarget.classList.remove("hidden")
      document.body.classList.add("overflow-hidden")
      return
    }

    lineItem.remove()
    this.invalidateCybershipCheck()
    this.scheduleLocalPreview()
    this.updateLineItemNumbers()
    this.calculateAllLineTotals()
  }

  isPersistedLineItem(lineItem) {
    if (lineItem.dataset.lineItemPersisted === "1") return true

    const idField = lineItem.querySelector('[data-purchase-order-form-target="lineItemId"]')
    if (idField?.value) return true

    const nestedIdField = lineItem.querySelector('input[type="hidden"][name$="[id]"]')
    return Boolean(nestedIdField?.value)
  }

  confirmLineItemRemoval() {
    const lineItem = this.pendingRemovalLineItem
    if (!lineItem) return

    this.markLineItemForRemoval(lineItem)
    this.closeRemovalModal()
    this.invalidateCybershipCheck()
    this.scheduleLocalPreview()
    this.updateLineItemNumbers()
    this.calculateAllLineTotals()
  }

  cancelLineItemRemoval() {
    this.pendingRemovalLineItem = null
    this.closeRemovalModal()
  }

  closeRemovalModal() {
    this.pendingRemovalLineItem = null
    if (this.hasRemovalModalTarget) {
      this.removalModalTarget.classList.add("hidden")
    }
    document.body.classList.remove("overflow-hidden")
  }

  undoLineItemRemoval(event) {
    const lineItem = event.target.closest('[data-purchase-order-form-target="lineItem"]')
    if (!lineItem) return

    const destroyField = lineItem.querySelector('[data-purchase-order-form-target="destroy"]')
    if (destroyField) destroyField.value = "0"

    lineItem.classList.remove("line-item-pending-removal", "opacity-60", "bg-gray-100", "border-amber-300")
    const removalNotice = lineItem.querySelector('[data-purchase-order-form-target="removalNotice"]')
    if (removalNotice) removalNotice.classList.add("hidden")

    const content = lineItem.querySelector('[data-purchase-order-form-target="lineItemContent"]')
    if (content) content.classList.remove("pointer-events-none", "opacity-70")

    const removeButton = lineItem.querySelector('[data-purchase-order-form-target="removeButton"]')
    if (removeButton) removeButton.classList.remove("hidden")

    this.invalidateCybershipCheck()
    this.scheduleLocalPreview()
    this.updateLineItemNumbers()
    this.calculateAllLineTotals()
  }

  markLineItemForRemoval(lineItem) {
    const destroyField = lineItem.querySelector('[data-purchase-order-form-target="destroy"]')
    if (destroyField) destroyField.value = "1"

    lineItem.classList.add("line-item-pending-removal", "opacity-60", "bg-gray-100", "border-amber-300")
    lineItem.querySelectorAll("[required]").forEach((field) => field.removeAttribute("required"))

    const content = lineItem.querySelector('[data-purchase-order-form-target="lineItemContent"]')
    if (content) content.classList.add("pointer-events-none", "opacity-70")

    const removalNotice = lineItem.querySelector('[data-purchase-order-form-target="removalNotice"]')
    if (removalNotice) removalNotice.classList.remove("hidden")

    const removeButton = lineItem.querySelector('[data-purchase-order-form-target="removeButton"]')
    if (removeButton) removeButton.classList.add("hidden")
  }

  isMarkedForDestroy(destroyField) {
    return destroyField.value === "1" || destroyField.value === "true"
  }

  isLineItemActive(lineItem) {
    const destroyField = lineItem.querySelector('[data-purchase-order-form-target="destroy"]')
    return !destroyField || !this.isMarkedForDestroy(destroyField)
  }

  handleFormChange() {
    this.invalidateCybershipCheck()
    this.scheduleLocalPreview()
  }

  scheduleLocalPreview() {
    if (!this.localPreviewUrlValue) return

    clearTimeout(this.localPreviewTimer)
    this.localPreviewTimer = setTimeout(() => this.refreshLocalPreview(), 300)
  }

  async refreshLocalPreview(event) {
    if (event) event.preventDefault()
    if (!this.localPreviewUrlValue || !this.hasLocalPreviewResultsTarget) return

    const refreshButton = this.hasLocalPreviewRefreshButtonTarget ? this.localPreviewRefreshButtonTarget : null
    const originalText = refreshButton?.textContent

    if (refreshButton) {
      refreshButton.disabled = true
      refreshButton.textContent = "Refreshing..."
    }

    try {
      const response = await fetch(this.localPreviewUrlValue, {
        method: "POST",
        body: this.previewFormData(),
        headers: {
          "X-CSRF-Token": this.csrfToken(),
          "Accept": "text/html"
        }
      })

      if (!response.ok) throw new Error("Local preview request failed")

      this.localPreviewResultsTarget.innerHTML = await response.text()
    } catch (error) {
      console.error("Local preview failed:", error)
      this.localPreviewResultsTarget.innerHTML = `
        <div class="bg-red-50 border border-red-200 rounded-lg p-4">
          <p class="text-sm font-medium text-red-800">Could not preview local changes</p>
          <p class="text-sm text-red-700 mt-1">Please try again.</p>
        </div>
      `
    } finally {
      if (refreshButton) {
        refreshButton.disabled = false
        refreshButton.textContent = originalText
      }
    }
  }

  invalidateCybershipCheck() {
    if (!this.requiresCybershipCheckValue) return

    this.cybershipCheckCompleted = false
    if (this.hasCybershipPreviewCheckedTarget) {
      this.cybershipPreviewCheckedTarget.value = "0"
    }
    if (this.hasCybershipPreviewResultsTarget) {
      this.cybershipPreviewResultsTarget.innerHTML = `
        <p class="text-sm text-amber-800">Form changed — re-compare with Cybership before saving.</p>
      `
    }
    this.updateSaveButtonState()
  }

  updateSaveButtonState() {
    if (!this.requiresCybershipCheckValue) return

    const enabled = this.cybershipCheckCompleted
    this.saveButtons().forEach((button) => {
      button.disabled = !enabled
      button.classList.toggle("opacity-50", !enabled)
      button.classList.toggle("cursor-not-allowed", !enabled)
      button.classList.toggle("pointer-events-none", !enabled)
      button.classList.toggle("bg-yellow-600", enabled)
      button.classList.toggle("hover:bg-yellow-700", enabled)
      button.classList.toggle("bg-gray-400", !enabled)
      button.title = enabled ? "" : "Re-compare with Cybership before saving"
    })

    if (this.hasSaveBlockedNoticeTarget) {
      this.saveBlockedNoticeTarget.classList.toggle("hidden", enabled)
    }
  }

  saveButtons() {
    if (this.saveButtonTargets.length > 0) return this.saveButtonTargets

    return Array.from(
      this.element.querySelectorAll('input[type="submit"][name="commit"], button[type="submit"][name="commit"]')
    )
  }

  async compareCybershipState(event) {
    event.preventDefault()
    if (!this.cybershipPreviewUrlValue) {
      console.error("Cybership preview URL is not configured")
      return
    }

    const button = event.currentTarget
    const originalText = button.textContent
    button.disabled = true
    button.textContent = "Comparing..."

    try {
      const response = await fetch(this.cybershipPreviewUrlValue, {
        method: "POST",
        body: this.previewFormData(),
        headers: {
          "X-CSRF-Token": this.csrfToken(),
          "Accept": "text/html"
        }
      })

      if (!response.ok) throw new Error("Preview request failed")

      const html = await response.text()
      if (this.hasCybershipPreviewResultsTarget) {
        this.cybershipPreviewResultsTarget.innerHTML = html
      }

      this.cybershipCheckCompleted = true
      if (this.hasCybershipPreviewCheckedTarget) {
        this.cybershipPreviewCheckedTarget.value = "1"
      }
      this.updateSaveButtonState()

      if (this.hasCybershipPreviewSectionTarget) {
        this.cybershipPreviewSectionTarget.scrollIntoView({ behavior: "smooth", block: "start" })
      }
    } catch (error) {
      console.error("Cybership preview failed:", error)
      this.cybershipCheckCompleted = false
      if (this.hasCybershipPreviewCheckedTarget) {
        this.cybershipPreviewCheckedTarget.value = "0"
      }
      this.updateSaveButtonState()

      if (this.hasCybershipPreviewResultsTarget) {
        this.cybershipPreviewResultsTarget.innerHTML = `
          <div class="bg-red-50 border border-red-200 rounded-lg p-4">
            <p class="text-sm font-medium text-red-800">Cybership comparison failed</p>
            <p class="text-sm text-red-700 mt-1">Please try again.</p>
          </div>
        `
      }
    } finally {
      button.disabled = false
      button.textContent = originalText
    }
  }

  previewFormData() {
    const formData = new FormData(this.element)
    // Edit forms include _method=patch; Rack::MethodOverride would turn our POST into PATCH.
    formData.delete("_method")
    return formData
  }

  csrfToken() {
    return document.querySelector('meta[name="csrf-token"]')?.content
  }

  calculateLineTotal(event) {
    const lineItem = event.target.closest('[data-purchase-order-form-target="lineItem"]')
    if (!lineItem) return

    const quantityInput = lineItem.querySelector('[data-purchase-order-form-target="quantity"]')
    const unitCostInput = lineItem.querySelector('[data-purchase-order-form-target="unitCost"]')
    const lineTotalTarget = lineItem.querySelector('[data-purchase-order-form-target="lineTotal"]')

    if (!quantityInput || !unitCostInput || !lineTotalTarget) return

    const quantity = parseFloat(quantityInput.value) || 0
    const unitCost = parseFloat(unitCostInput.value) || 0
    const total = quantity * unitCost

    lineTotalTarget.textContent = this.formatCurrency(total)
    this.updateOrderSummary()
    this.scheduleLocalPreview()
  }

  calculateAllLineTotals() {
    this.lineItemTargets.forEach(lineItem => {
      if (this.isLineItemActive(lineItem)) {
        const quantityInput = lineItem.querySelector('[data-purchase-order-form-target="quantity"]')
        const unitCostInput = lineItem.querySelector('[data-purchase-order-form-target="unitCost"]')
        const lineTotalTarget = lineItem.querySelector('[data-purchase-order-form-target="lineTotal"]')

        if (quantityInput && unitCostInput && lineTotalTarget) {
          const quantity = parseFloat(quantityInput.value) || 0
          const unitCost = parseFloat(unitCostInput.value) || 0
          const total = quantity * unitCost
          lineTotalTarget.textContent = this.formatCurrency(total)
        }
      }
    })
    this.updateOrderSummary()
  }

  updateOrderSummary() {
    const subtotal = this.calculateSubtotal()
    const costs = this.getOrderCosts()
    const discountInDollars = this.calculateDiscountInDollars(subtotal, costs.discountAmount, costs.discountType)
    const grandTotal = (subtotal + (costs.shippingCost || 0) + (costs.additionalCosts || 0) - discountInDollars) || 0

    this.updateSummaryDisplay({
      subtotal,
      shippingCost: costs.shippingCost,
      additionalCosts: costs.additionalCosts,
      discountAmount: costs.discountAmount,
      discountType: costs.discountType,
      discountInDollars,
      grandTotal
    })
  }

  calculateSubtotal() {
    let subtotal = 0
    this.lineItemTargets.forEach(lineItem => {
      if (this.isLineItemActive(lineItem)) {
        const quantityInput = lineItem.querySelector('[data-purchase-order-form-target="quantity"]')
        const unitCostInput = lineItem.querySelector('[data-purchase-order-form-target="unitCost"]')

        if (quantityInput && unitCostInput) {
          const quantity = parseFloat(quantityInput.value) || 0
          const unitCost = parseFloat(unitCostInput.value) || 0
          subtotal += quantity * unitCost
        }
      }
    })
    return subtotal
  }

  getOrderCosts() {
    return {
      shippingCost: this.getFieldValue('shipping_cost'),
      additionalCosts: this.getFieldValue('additional_costs'),
      discountAmount: this.getFieldValue('discount_amount'),
      discountType: this.getFieldValue('discount_type')
    }
  }

  calculateDiscountInDollars(subtotal, discountAmount, discountType) {
    // Calculate discount in dollars - percentage only applies to subtotal, not shipping/additional costs
    // erik/TODO: Need to verify exactly what quantities the discount needs to apply to
    if (discountAmount <= 0) return 0

    if (discountType === 'percentage') {
      // Percentage discount only applies to the subtotal
      return subtotal * (discountAmount / 100.0 || 0);
    } else {
      // Fixed amount discount
      return discountAmount
    }
  }

  updateSummaryDisplay(values) {
    // Update main summary values
    this.updateElementText('[data-po-summary-target="subtotal"]', this.formatCurrency(values.subtotal))
    this.updateElementText('[data-po-summary-target="shipping"]', this.formatCurrency(values.shippingCost))
    this.updateElementText('[data-po-summary-target="additionalCosts"]', this.formatCurrency(values.additionalCosts))
    this.updateElementText('[data-po-summary-target="grandTotal"]', this.formatCurrency(values.grandTotal))

    // Update discount display
    this.updateDiscountRow(values.discountInDollars, values.discountAmount, values.discountType)
  }

  updateElementText(selector, text) {
    const element = document.querySelector(selector)
    if (element) element.textContent = text
  }

  updateDiscountRow(discountInDollars, discountAmount, discountType) {
    const discountRow = document.querySelector('[data-po-summary-target="discountRow"]')
    const discountLabel = document.querySelector('[data-po-summary-target="discountLabel"]')
    const discountValue = document.querySelector('[data-po-summary-target="discount"]')

    if (discountInDollars > 0) {
      if (discountRow) discountRow.style.display = 'table-row'
      if (discountValue) discountValue.textContent = `-${this.formatCurrency(discountInDollars)}`

      if (discountLabel) {
        if (discountType === 'percentage') {
          discountLabel.textContent = `Discount (${discountAmount}%)`
        } else {
          discountLabel.textContent = 'Discount'
        }
      }
    } else {
      if (discountRow) discountRow.style.display = 'none'
    }
  }

  getFieldValue(fieldName) {
    const input = this.element.querySelector(`input[name*="[${fieldName}]"], select[name*="[${fieldName}]"]`)
    if (fieldName === 'discount_type') {
      return input?.value || 'amount'
    }
    return parseFloat(input?.value) || 0
  }

    updateLineItemNumbers() {
    const allLineItems = this.lineItemsContainerTarget.querySelectorAll('[data-purchase-order-form-target="lineItem"]')

    const visibleLineItems = Array.from(allLineItems).filter(item => this.isLineItemActive(item))

    visibleLineItems.forEach((item, idx) => {
      const header = item.querySelector('h4')
      if (header) header.textContent = `Line Item ${idx + 1}`
    })
  }

  updateDiscountDisplay(event) {
    if (!this.hasDiscountTypeTarget || !this.hasDiscountPrefixTarget || !this.hasDiscountAmountTarget) {
      return
    }

    const discountType = this.discountTypeTarget.value
    const isPercentage = discountType === 'percentage'
    const hasDiscount = discountType !== '' && discountType !== null

    // Handle no discount case
    if (!hasDiscount) {
      this.discountAmountTarget.disabled = true
      this.discountAmountTarget.value = ''
      this.discountAmountTarget.placeholder = 'No discount'
      this.discountPrefixTarget.style.display = 'none'
      this.discountAmountTarget.classList.remove('pl-7')
      this.discountAmountTarget.classList.add('pr-3')
      this.validateDiscountAmount()
      this.updateOrderSummary()
      return
    }

    // Enable field for discount types
    this.discountAmountTarget.disabled = false

    // Handle value clearing/restoration logic on change events
    if (event && event.type === 'change') {
      if (discountType === this.originalDiscountType && this.originalDiscountAmount) {
        // Restore original value when switching back to the original type
        this.discountAmountTarget.value = this.originalDiscountAmount
      } else {
        // Clear value when switching to a different type than original
        this.discountAmountTarget.value = ''
      }
    }

    this.discountPrefixTarget.style.display = 'flex'
    this.discountAmountTarget.classList.add('pl-7')
    this.discountAmountTarget.classList.remove('pr-3')
    this.discountAmountTarget.placeholder = '0.00'
    this.discountAmountTarget.step = '0.01'
    this.discountAmountTarget.min = '0'

    if (isPercentage) {
      // Show percentage display with left-justified % sign
      this.discountAmountTarget.max = '100'

      // Update the prefix to show % instead of $
      const prefixSpan = this.discountPrefixTarget.querySelector('span')
      if (prefixSpan) {
        prefixSpan.textContent = '%'
      }
    } else {
      // Show fixed amount display
      this.discountAmountTarget.removeAttribute('max')

      // Update the prefix to show $ instead of %
      const prefixSpan = this.discountPrefixTarget.querySelector('span')
      if (prefixSpan) {
        prefixSpan.textContent = '$'
      }
    }

    this.validateDiscountAmount()
    this.updateOrderSummary()
    this.scheduleLocalPreview()
  }

  validateDiscountAmount() {
    if (!this.hasDiscountAmountTarget || !this.hasDiscountTypeTarget) {
      return
    }

    const discountType = this.discountTypeTarget.value
    let value = this.discountAmountTarget.value
    const isPercentage = discountType === 'percentage'

    // Enforce 2 decimal places maximum
    if (value.includes('.')) {
      const parts = value.split('.')
      if (parts[1] && parts[1].length > 2) {
        value = parts[0] + '.' + parts[1].substring(0, 2)
        this.discountAmountTarget.value = value
      }
    }

    const amount = parseFloat(value) || 0

    if (isPercentage && amount > 100) {
      this.discountAmountTarget.value = '100.00'
      this.showDiscountValidationMessage('Percentage cannot exceed 100%')
    } else if (amount < 0) {
      this.discountAmountTarget.value = '0.00'
      this.showDiscountValidationMessage('Discount cannot be negative')
    }

    // Update order summary after validation
    this.updateOrderSummary()
    this.scheduleLocalPreview()
  }

  showDiscountValidationMessage(message) {
    // Remove any existing validation message first if it's present
    const existingMessage = this.discountFieldWrapperTarget.querySelector('.discount-validation-message')
    if (existingMessage) {
      existingMessage.remove()
    }

    const validationMessage = document.createElement('div')
    validationMessage.className = 'discount-validation-message text-sm text-red-600 mt-1'
    validationMessage.textContent = message

    // Append to the field wrapper (outside the input container)
    this.discountFieldWrapperTarget.appendChild(validationMessage)

    // Remove message after 3 seconds
    // erik/TODO: I'm not the biggest fan of this, feels a bit weird as a user
    // but let's just use this for now.
    setTimeout(() => {
      if (validationMessage && validationMessage.parentNode) {
        validationMessage.remove()
      }
    }, 3000)
  }

  warehouseChanged() {
    // erik/TODO: Filter products by warehouse
    // make AJAX calls to load inventory_product_locations for the selected warehouse
    // Upon second thought, we want to populate the "Product" dropdown field
    // to display the _entirety_ of the catalog, not just the IP.
    console.error("NOT YET IMPLEMENTED")
  }

  formatCurrency(amount) {
    // erik/TODO: currency display, should we use a library for this or is this just fine?
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 4
    }).format(amount)
  }

  confirmOrder(event) {
    const confirmMessage = event.currentTarget.dataset.confirmMessage

    if (!confirm(confirmMessage)) {
      event.preventDefault()
      event.stopPropagation()
      return false
    }
  }

  handleProductSelected(event) {
    const { product, input, hiddenField } = event.detail

    if (!product) {
      return
    }

    // Find the line item that contains this input
    const lineItem = input.closest('[data-purchase-order-form-target="lineItem"]')
    if (!lineItem) {
      return
    }

    const syncStatusTarget = lineItem.querySelector('[data-purchase-order-form-target="syncStatus"]')
    if (!syncStatusTarget) {
      return
    }

    // Display sync status with the product data we just received
    this.displaySyncStatus(product, syncStatusTarget, lineItem)
  }

  onProductSelected(event) {
    // This method is kept for backward compatibility but may not be needed
    // The typeahead:selected event should handle this now
  }

  displaySyncStatus(product, syncStatusTarget, lineItem) {
    const hasExternalId = product.external_id && product.external_id.trim() !== ''
    const productId = product.id
    const productSku = product.sku || ''

    if (hasExternalId) {
      syncStatusTarget.innerHTML = `
        <div class="flex items-center text-sm text-green-700">
          <span class="mr-2">✅</span>
          <span>Product exists in Cybership, ready to add</span>
        </div>
      `
      syncStatusTarget.className = 'sync-status mt-2'
      this.removeProductError(lineItem)
    } else {
      syncStatusTarget.innerHTML = `
        <div class="space-y-2">
          <div class="flex items-center text-sm text-yellow-700">
            <span class="mr-2">⚠️</span>
            <span>Product is not synced to Cybership. PO will not be created properly until this is addressed</span>
          </div>
          <div class="flex items-center space-x-2">
            <button type="button"
                    class="inline-flex items-center px-2 py-1 border border-yellow-300 rounded text-xs font-medium text-yellow-700 bg-yellow-50 hover:bg-yellow-100 focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-yellow-500"
                    data-product-sku="${productSku}">
              <svg class="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"></path>
              </svg>
              Refresh Sync Status
            </button>
            <a href="/admin/inventory_products/${productId}/edit"
               target="_blank"
               rel="noopener noreferrer"
               class="inline-flex items-center px-2 py-1 border border-yellow-300 rounded text-xs font-medium text-yellow-700 bg-yellow-50 hover:bg-yellow-100 focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-yellow-500">
              <svg class="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"></path>
              </svg>
              Edit Product
            </a>
          </div>
        </div>
      `
      syncStatusTarget.className = 'sync-status mt-2'
      this.addProductError(lineItem, product)
    }

    syncStatusTarget.style.display = 'block'
    this.updateValidationErrors()
  }

  addProductError(lineItem, product) {
    const productId = lineItem.querySelector('input[type="hidden"][name*="[inventory_product_id]"]')?.value
    const errorKey = `product_${productId}`

    // Remove existing error for this product if any
    this.removeProductError(lineItem)

    // Add new error
    this.errors.push({
      key: errorKey,
      message: `${product.name || product.sku} is not synced to Cybership. Please sync this product in the Products tab before ordering.`,
      lineItem: lineItem
    })
  }

  removeProductError(lineItem) {
    const productId = lineItem.querySelector('input[type="hidden"][name*="[inventory_product_id]"]')?.value
    const errorKey = `product_${productId}`

    this.errors = this.errors.filter(error => error.key !== errorKey)
  }


  updateValidationErrors() {
    if (this.errors.length === 0) {
      if (this.hasValidationErrorsTarget) {
        this.validationErrorsTarget.style.display = 'none'
      }
      return
    }

    // Show errors
    if (this.hasValidationErrorsTarget) {
      this.validationErrorsTarget.style.display = 'block'
    }

    // Update error list
    if (this.hasErrorListTarget) {
      this.errorListTarget.innerHTML = this.errors.map(error =>
        `<li>${error.message}</li>`
      ).join('')
    }
  }

  validateBeforeSubmit(event) {
    if (this.requiresCybershipCheckValue && !this.cybershipCheckCompleted) {
      event.preventDefault()
      event.stopPropagation()

      if (this.hasCybershipPreviewSectionTarget) {
        this.cybershipPreviewSectionTarget.scrollIntoView({ behavior: "smooth", block: "start" })
      }

      return false
    }

    const submitButton = event.submitter
    const isOrderButton = submitButton && submitButton.name === 'commit[order]'

    if (!isOrderButton) {
      return true
    }

    if (this.errors.length > 0) {
      event.preventDefault()
      event.stopPropagation()

      // Scroll to errors
      if (this.hasValidationErrorsTarget) {
        this.validationErrorsTarget.scrollIntoView({ behavior: 'smooth', block: 'start' })
      }

      return false
    }
  }

  async refreshProductSyncStatus(event) {
    const button = event.target.closest('button[data-product-sku]')
    if (!button) return

    const productSku = button.dataset.productSku
    const lineItem = button.closest('[data-purchase-order-form-target="lineItem"]')
    const syncStatusTarget = lineItem.querySelector('[data-purchase-order-form-target="syncStatus"]')

    if (!productSku || !syncStatusTarget) {
      return
    }

    // Disable button and show loading state
    button.disabled = true
    const originalContent = syncStatusTarget.innerHTML
    syncStatusTarget.innerHTML = `
      <div class="flex items-center text-sm text-gray-600">
        <svg class="animate-spin -ml-1 mr-2 h-4 w-4 text-gray-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
          <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
          <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
        </svg>
        <span>Checking sync status...</span>
      </div>
    `

    try {
      // Search for the product by SKU
      const response = await fetch(`/admin/inventory_products/search?query=${encodeURIComponent(productSku)}`)
      if (response.ok) {
        const results = await response.json()
        // Get the first item from the search results
        const product = results && results.length > 0 ? results[0] : null

        if (product) {
          // Update the sync status with the fresh data
          this.displaySyncStatus(product, syncStatusTarget, lineItem)
        } else {
          throw new Error('Product not found in search results')
        }
      } else {
        throw new Error('Failed to search for product')
      }
    } catch (error) {
      console.error('Error refreshing product sync status:', error)
      syncStatusTarget.innerHTML = originalContent
      button.disabled = false
    }
  }
}
